import { TicketChannel, type TicketMessageMentionDto, TicketPriority, TicketStatus } from "@/api/tickets";
import type { TranslationKey } from "@/locales/i18n";
import type { TicketAttachmentView } from "./ticket-attachments";

/**
 * 后端枚举 → 界面文案/样式的唯一映射，工单列表与详情共用，避免多处漂移。
 * 文案跟 `TicketEnums.cs` 的语义对齐，不要按界面需要另起一套。
 */

/**
 * 事件文案的一段。事件句子里引用到的具体值——人名、客服组、分类、标签、状态、优先级——
 * 单独成段并标 `emphasis`，由渲染层着重显示，句子里的连接词保持弱化。
 */
export type TicketEventSegment = {
  text: string;
  emphasis?: boolean;
};

/**
 * 活动时间线的展示模型。条目都来自 `GET tickets/{id}/timeline`：
 * `note` 对应消息条目，`event` 对应状态流转 / 指派 / 分类变更这类审计事件。
 */
export type TicketActivityItem =
  | {
      id: string;
      kind: "event";
      author?: string;
      avatarUrl?: string | null;
      initials?: string;
      icon: string;
      segments: TicketEventSegment[];
      time: string;
      /** 本次进入已解决时保存的快照；历史状态事件没有此字段。 */
      resolution?: { result: string; feedbackMethodLabel: string; feedbackMethodIcon?: string };
    }
  | {
      id: string;
      kind: "note";
      author: string;
      avatarUrl?: string | null;
      initials: string;
      /** 保留服务端 `@{agentId}` token，展示时才能准确区分重名成员。 */
      content: string;
      mentions?: TicketMessageMentionDto[];
      time: string;
      /** 发送中的条目是本地文件，已发出的是服务端附件；两者同一个展示模型。 */
      attachments?: TicketAttachmentView[];
      deliveryState?: "failed" | "sending" | "sent";
    };

const UNKNOWN_STATUS = { labelKey: "tickets.status.unknown", className: "bg-muted text-muted-foreground", icon: "tabler:help-circle" } satisfies {
  labelKey: TranslationKey;
  className: string;
  icon: string;
};
const UNKNOWN_PRIORITY = {
  labelKey: "tickets.priority.normal",
  className: "text-foreground",
  icon: "flat-color-icons:medium-priority",
} satisfies { labelKey: TranslationKey; className: string; icon: string };
const UNKNOWN_CHANNEL = { labelKey: "tickets.channel.unknown", icon: "tabler:help-circle" } satisfies { labelKey: TranslationKey; icon: string };

export const STATUS_PRESENTATION: Record<number, { labelKey: TranslationKey; icon: string; className: string }> = {
  [TicketStatus.New]: { labelKey: "tickets.status.new", icon: "tabler:circle-dashed-plus", className: "bg-secondary text-secondary-foreground" },
  [TicketStatus.Open]: { labelKey: "tickets.status.open", icon: "tabler:circle-dot", className: "bg-primary/10 text-primary" },
  [TicketStatus.Pending]: { labelKey: "tickets.status.pending", icon: "tabler:circle-dot", className: "bg-warning/10 text-warning" },
  [TicketStatus.Resolved]: { labelKey: "tickets.status.resolved", icon: "tabler:circle-check", className: "bg-success/10 text-success" },
  [TicketStatus.Closed]: { labelKey: "tickets.status.closed", icon: "tabler:circle-dashed-check", className: "bg-purple-100 text-purple-500" },
  [TicketStatus.Cancelled]: { labelKey: "tickets.status.cancelled", icon: "tabler:circle-dashed-minus", className: "bg-muted text-muted-foreground" },
};

export const PRIORITY_PRESENTATION: Record<number, { labelKey: TranslationKey; icon: string; className: string }> = {
  [TicketPriority.Low]: { labelKey: "tickets.priority.low", icon: "emojione:white-exclamation-mark", className: "text-muted-foreground" },
  [TicketPriority.Normal]: { labelKey: "tickets.priority.normal", icon: "emojione:exclamation-mark", className: "text-foreground/50" },
  [TicketPriority.High]: { labelKey: "tickets.priority.high", icon: "emojione:double-exclamation-mark", className: "text-red-600" },
  [TicketPriority.Urgent]: { labelKey: "tickets.priority.urgent", icon: "emojione:fire", className: "text-destructive" },
};

export const TICKET_PRIORITY_VALUES: ReadonlyArray<TicketPriority> = [TicketPriority.Low, TicketPriority.Normal, TicketPriority.High, TicketPriority.Urgent];

export const CHANNEL_PRESENTATION: Record<number, { labelKey: TranslationKey; icon: string }> = {
  [TicketChannel.Web]: { labelKey: "tickets.channel.web", icon: "tabler:world-www" },
  [TicketChannel.Phone]: { labelKey: "tickets.channel.phone", icon: "tabler:phone" },
  [TicketChannel.Api]: { labelKey: "tickets.channel.api", icon: "tabler:api" },
};

export function statusPresentation(status: number) {
  return STATUS_PRESENTATION[status] ?? UNKNOWN_STATUS;
}

export function priorityPresentation(priority: number) {
  return PRIORITY_PRESENTATION[priority] ?? UNKNOWN_PRIORITY;
}

export function channelPresentation(channel: number) {
  return CHANNEL_PRESENTATION[channel] ?? UNKNOWN_CHANNEL;
}

/** 列表时间列：只到分钟，够紧凑；跨年时补上年份。 */
export function formatDateTime(iso: string | null | undefined, intlLocale: string): string {
  if (!iso) {
    return "—";
  }
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) {
    return "—";
  }
  const sameYear = value.getFullYear() === new Date().getFullYear();
  return value.toLocaleString(intlLocale, {
    year: sameYear ? undefined : "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** 列表次要信息只保留单号最后一段，完整单号仍通过 title/aria-label 提供。 */
export function formatShortTicketNumber(number: string): string {
  const normalized = number.trim();
  const lastSegment = normalized.split("-").filter(Boolean).at(-1);
  return lastSegment ? `#${lastSegment}` : normalized;
}
